/**
 * The consent singleton: the one place that knows the visitor's current
 * status, persists their decision, and notifies subscribers.
 *
 * The banner is GEO-GATED to California and Florida. Everyone else has their
 * tags granted without ever being asked (`not-required`). See
 * `consentPolicy.ts` for the policy and `consentGeo.ts` for how the region is
 * resolved. Tags stay withheld for everyone until that question is answered.
 *
 * Deliberately a plain module-level store rather than React context state.
 * `gaPixel` / `metaPixel` are non-React modules called from hooks, effects and
 * plain functions; they need a synchronous `isAdvertisingGranted()` with no
 * provider in scope. React reads the same store through `useConsent`
 * (`useSyncExternalStore`), so there is exactly one source of truth.
 *
 * ## Initialization order
 *
 * `public/index.html` runs its bootstrap first and publishes
 * `window.__theaConsent`. We adopt that status rather than re-deriving it: the
 * bootstrap ran the same version check against the same storage before the
 * tags loaded, and the live tag state (Consent Mode defaults, whether
 * fbevents.js was injected) already reflects its answer. Recomputing here
 * could disagree with what the tags actually did. If the handshake is absent —
 * jsdom tests, or a future SSR/prerender host — we read storage directly with
 * the same TypeScript the bootstrap mirrors.
 *
 * ## Consent receipts — what is and isn't stored here
 *
 * On decision we persist a `ConsentReceipt` to localStorage:
 * `{type, version, status, acceptedAt, userAgent}`. The launch gap analysis
 * (2026-08-19, "Audit trail") also asks for `ip`, and for the record to live
 * server-side under `theaWebUser/{uid}/consent/{event}` so it survives a
 * cleared browser and is admissible.
 *
 * Neither is done here, on purpose:
 *   - a browser cannot read its own egress IP without calling a third party,
 *     and calling a third party pre-consent is the exact thing this feature
 *     prevents. Only the server can stamp it;
 *   - writing the receipt server-side needs a new callable, and this branch is
 *     scoped web-only (a sibling branch owns the backend).
 *
 * FOLLOW-UP: a `theaWebRecordConsent` callable that takes this receipt, stamps
 * `ip` from the request context, and writes it under the user's consent
 * subcollection. `getReceipt()` below returns exactly the payload it should
 * take, so wiring it is an additive change with no refactor.
 */
import {
  CONSENT_STORAGE_KEY,
  advertisingAllowed,
  analyticsAllowed,
  buildReceipt,
  parseReceipt,
  type ConsentReceipt,
  type ConsentStatus,
} from './consentPolicy';
import { fetchConsentGeo, type GeoOutcome } from './consentGeo';
import { applyConsentToTags, readBootstrap } from './consentTags';

/** Where the geo lookup has got to. Drives whether the banner may appear. */
export type GeoPhase =
  /** Not needed (an explicit choice is already on file) or not started yet. */
  | 'idle'
  /** In flight. The banner stays hidden so it cannot flash for everyone. */
  | 'resolving'
  /** Answered, or failed and fell back. */
  | 'settled';

export interface ConsentState {
  status: ConsentStatus;
  /** True when the banner should be on screen. */
  bannerVisible: boolean;
  geoPhase: GeoPhase;
}

type Listener = () => void;

const listeners = new Set<Listener>();

/**
 * `useSyncExternalStore` compares snapshots by reference, so the snapshot has
 * to be a stable object that is only replaced when something actually changes.
 */
let state: ConsentState | null = null;
let receipt: ConsentReceipt | null = null;
/** Shared promise so concurrent mounts make one geo call, not several. */
let inFlight: Promise<void> | null = null;
/**
 * The last geo answer, kept for TELEMETRY ONLY — never read as policy.
 *
 * `bannerVisible: true` has two completely different meanings that used to be
 * indistinguishable once they reached the UI: "the server says this visitor is
 * in CA or FL" and "we never got an answer, so we are asking to be safe".
 * THE-521 was the second one firing for most mobile traffic for weeks, and
 * nothing in analytics could tell them apart — which is why it arrived through
 * a user interview instead of a dashboard. `ConsentBannerHost` reads this and
 * stamps it on the impression event.
 */
let geoOutcome: GeoOutcome | null = null;

function readStoredReceipt(): ConsentReceipt | null {
  try {
    return parseReceipt(window.localStorage.getItem(CONSENT_STORAGE_KEY));
  } catch {
    // Safari private mode / storage disabled. Treated as "no decision on
    // file", which fails safe to withholding.
    return null;
  }
}

function computeInitialState(): ConsentState {
  if (typeof window === 'undefined') {
    // Off-window (SSR/prerender): nothing can fire anyway, and there is no
    // one to prompt. Withhold and show nothing.
    return { status: 'pending', bannerVisible: false, geoPhase: 'idle' };
  }

  receipt = readStoredReceipt();
  const bootstrap = readBootstrap();
  const status: ConsentStatus = bootstrap
    ? bootstrap.status
    : receipt
      ? receipt.status
      : 'pending';

  // NOTE the banner is NOT shown just because status is 'pending'. Geo has to
  // answer first, otherwise the banner would flash for every visitor on earth
  // for the length of one round trip — which is the whole thing this feature
  // exists to stop.
  return { status, bannerVisible: false, geoPhase: 'idle' };
}

function ensureState(): ConsentState {
  if (state === null) state = computeInitialState();
  return state;
}

function setState(next: ConsentState): void {
  state = next;
  listeners.forEach((listener) => listener());
}

/** Current snapshot. Reference-stable until something changes. */
export function getConsentState(): ConsentState {
  return ensureState();
}

/** Subscribe to changes. Returns an unsubscribe function. */
export function subscribeConsent(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The stored receipt, or null. Shape the server-side follow-up will accept. */
export function getReceipt(): ConsentReceipt | null {
  ensureState();
  return receipt;
}

/**
 * How the geo question was answered this session, or null if it was never
 * asked (an explicit decision was already on file).
 *
 * TELEMETRY ONLY. Nothing may branch on this: the banner's visibility is
 * decided in `ensureGeoResolved` and reading the outcome a second time to
 * re-decide it is how the two copies drift apart.
 */
export function getGeoOutcome(): GeoOutcome | null {
  return geoOutcome;
}

/** May app code emit GA4 events right now? */
export function isAnalyticsGranted(): boolean {
  return analyticsAllowed(ensureState().status);
}

/** May app code load / fire the Meta Pixel right now? */
export function isAdvertisingGranted(): boolean {
  return advertisingAllowed(ensureState().status);
}

function decide(status: 'granted' | 'denied'): void {
  ensureState();

  receipt = buildReceipt(status);
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(receipt));
  } catch {
    // Storage unavailable — the decision still applies for this page load, we
    // just have to ask again next visit. Better than dropping the click.
  }

  applyConsentToTags(status);
  setState({ ...ensureState(), status, bannerVisible: false, geoPhase: 'settled' });
}

/** Visitor clicked Accept. */
export function acceptConsent(): void {
  decide('granted');
}

/** Visitor clicked Decline. */
export function declineConsent(): void {
  decide('denied');
}

/**
 * Re-open the banner so the visitor can change or withdraw a prior decision.
 *
 * Withdrawal has to be as easy as granting under CCPA/CPRA and the newer state
 * regimes, so this needs a permanent, discoverable entry point — see the
 * "Cookie preferences" control on the privacy page.
 *
 * The prior decision stays in force (tags keep behaving as they were) until a
 * new choice is made; only re-showing the banner is immediate. Otherwise
 * merely opening the control would be a silent opt-out.
 */
export function reopenConsentBanner(): void {
  setState({ ...ensureState(), bannerVisible: true, geoPhase: 'settled' });
}

/**
 * Resolve geo once, then either auto-grant or reveal the banner.
 *
 * ## The rule that is easiest to get wrong
 *
 * **An explicit choice always wins over geo.** If someone declined under the
 * previous worldwide banner and then moved (or we simply resolve them as
 * outside CA/FL), auto-granting would silently overturn a preference they
 * actively stated — which is a worse wrong than the problem geo is solving.
 * So this returns immediately unless status is exactly 'pending', which is the
 * only value that means "no decision on file".
 *
 * Idempotent: concurrent callers share one in-flight promise, and a settled
 * lookup is never repeated.
 */
export async function ensureGeoResolved(): Promise<void> {
  const current = ensureState();

  // 'granted' / 'denied' are user decisions; 'not-required' is already settled.
  if (current.status !== 'pending') return;
  if (current.geoPhase !== 'idle') return inFlight ?? undefined;

  setState({ ...current, geoPhase: 'resolving' });

  inFlight = (async () => {
    // `fetchConsentGeo` is documented never to reject, but this path decides
    // whether someone gets tracked without being asked — so it does not take
    // that on faith. A throw here must land on "show the banner", never on an
    // unhandled rejection that leaves the visitor un-asked AND un-tracked.
    let outcome: GeoOutcome;
    try {
      outcome = await fetchConsentGeo();
    } catch {
      outcome = { consentRequired: true, resolved: false, source: 'fail-safe' };
    }
    geoOutcome = outcome;

    // Re-read: the visitor may have decided while the call was in flight (the
    // banner can be reopened from the privacy page). Their click wins.
    const latest = ensureState();
    if (latest.status !== 'pending') {
      setState({ ...latest, geoPhase: 'settled' });
      return;
    }

    if (outcome.consentRequired) {
      // Gated, or we could not tell. Ask — unless they already waved it away
      // this session, in which case asking again immediately is exactly the
      // nagging the dismiss button exists to prevent.
      setState({
        ...latest,
        bannerVisible: !readDismissed(),
        geoPhase: 'settled',
      });
      return;
    }

    // Outside the gated states: consent is not required of this visitor, so
    // grant the tags without ever asking. Deliberately NOT persisted — see the
    // `not-required` docs in consentPolicy: it is a geo fact, not a decision,
    // and it is re-derived once per session.
    applyConsentToTags('not-required');
    setState({ status: 'not-required', bannerVisible: false, geoPhase: 'settled' });
  })();

  try {
    await inFlight;
  } catch {
    // Unreachable given the guard above; kept so a future edit inside the IIFE
    // cannot turn this into an unhandled rejection at every call site.
    setState({ ...ensureState(), bannerVisible: true, geoPhase: 'settled' });
  } finally {
    inFlight = null;
  }
}

/**
 * Session key recording that the visitor pressed the X. NOT a consent record.
 */
const DISMISSED_SESSION_KEY = 'thea.consentDismissed';

function readDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(DISMISSED_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Visitor pressed the X. "Not now."
 *
 * ## What dismissal means, and why
 *
 * It is NOT consent: nothing is granted, `status` stays `pending`, Consent Mode
 * stays denied and the Pixel stays uninjected. That much is not negotiable —
 * a dismiss that silently granted would be the textbook dark pattern.
 *
 * It is also NOT a decline, which is the less obvious half. Recording a
 * decline would overstate what the visitor said: they said "not now", not
 * "no". It would also mean we never ask again, which is worse for someone who
 * would have accepted had they read it a moment later. So we persist nothing
 * durable — no receipt is written — and the banner returns on a later visit.
 *
 * The suppression is per SESSION, in sessionStorage, so "easily dismissable"
 * actually holds: the banner does not spring back on the next route change or
 * reload within the same tab. A new session asks again, which is the honest
 * consequence of the visitor never having answered.
 *
 * Geo is unaffected: a dismissed visitor outside CA/FL is still auto-granted
 * on the normal path, because dismissal only suppresses the prompt, never the
 * region decision.
 *
 * ## Why the hide is unconditional but the suppression flag is not (THE-59)
 *
 * `reopenConsentBanner` — "Change your cookie choices" on /privacy — is the one
 * path that shows the banner to somebody whose status is NOT 'pending'. Guarding
 * the whole function on 'pending' made the X and the swipe inert there, and the
 * link the visitor arrived through is hidden while the banner is up, so the only
 * exits were Accept, Decline or a reload. Backing out is not a decision, so the
 * withdraw surface must not corner anyone into making one.
 *
 * So the two halves are guarded separately:
 *
 * - **Hiding is always safe** and always happens. It writes nothing durable and
 *   moves no signal, so it cannot overwrite a decision that already landed.
 * - **The session suppression flag is 'pending'-only.** It means "this visitor
 *   was asked and said not now". A decided visitor was never asked; recording it
 *   for them would also wrongly suppress a real re-prompt if their receipt were
 *   later cleared or aged out by a policy-version bump.
 */
export function dismissConsentBanner(): void {
  const current = ensureState();

  if (current.status === 'pending') {
    try {
      window.sessionStorage.setItem(DISMISSED_SESSION_KEY, '1');
    } catch {
      // Storage unavailable — the banner hides for this page load only. Still
      // better than a dismiss button that appears not to work.
    }
  }

  // NOTE: no receipt, and no applyConsentToTags call, in EITHER case. A pending
  // visitor stays pending with every tag denied; a decided visitor keeps the
  // decision and the tag state they already had. Only the banner moves.
  setState({ ...current, bannerVisible: false, geoPhase: 'settled' });
}

/** Test-only: drop all state so the next read recomputes from scratch. */
export function __resetConsentStoreForTests(): void {
  state = null;
  receipt = null;
  inFlight = null;
  geoOutcome = null;
  listeners.clear();
}
